import { useCallback, useEffect, useState, VoidFunctionComponent } from 'react';
import { TextureLoader } from 'three';
import { useAccount, useNetwork, useDisconnect, useProvider } from 'wagmi';
import { useKey, useMount } from 'react-use';
import { BigNumber, ethers } from 'ethers';
import { OnlyBot } from '@animavirtuality/onlybots-core';
import { OnlyBotThree } from '@animavirtuality/onlybots-three';
import { TitleScreen } from '@/components/scenes/TitleScreen';
import { StorySequence } from '@/components/scenes/StorySequence';
import { Connect } from '@/components/scenes/Connect';
import { BeginTest } from '@/components/scenes/BeginTest';
import { Quiz } from '@/components/scenes/Quiz';
import { chainId, escrowContractAddress, featuredBotsJSON, MOSAIC_TRANSITION_DURATION } from '@/lib/globals';
import { CodeInput } from '@/components/scenes/CodeInput';
import { Loading } from '@/components/scenes/Loading';
import { StartScreen } from '@/components/scenes/StartScreen';
import { useDeviceOrientation } from '@/hooks/useDeviceOrientation';
import { SoldOut } from '@/components/scenes/SoldOut';
import { FeaturedBot, FeaturedBotJson, OnlybotsScene } from '@/lib/types';
import { useDisposeOnUnmount } from '@/hooks/useDisposeOnUnmount';
import { formatVoxelMaterialsFromBot } from '@/lib/voxelBot';
import gameStartSound from '@/sounds/ob-sfx-game-start.mp3';
import { useMusic } from '@/hooks/useMusic';
import { PerformRecaptcha } from '@/components/scenes/PerformRecaptcha';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { GA } from '@/lib/ga';
import { useMute } from '@/hooks/useMute';
import { deviceProperties } from '@/lib/deviceProperties';
import { EditionLoading } from '@/components/scenes/EditionLoading';
import { RequestOrientation } from '@/components/scenes/RequestOrientation';
import { Edition } from '@/components/scenes/Edition';
import { useEffectContext } from '@/hooks/useEffectContext';
import { NotFound } from '@/components/scenes/NotFound';
import { PostProcessingEffect } from '@/components/PostProcessingEffect';
import { presets } from '@/lib/presets';
import { useRouterContext } from '@/context/RouterContext';
import { useDprContext } from '@/context/DprContext';
import { IncubatorClosed } from '@/components/scenes/IncubatorClosed';
import { Reveal } from '@/components/scenes/Reveal';
import { AlreadyMinted } from '@/components/scenes/AlreadyMinted';
import { Mint } from '@/components/scenes/Mint';
import { useOnlybotsBackend } from '@/hooks/useOnlybotsBackend';
import { Download } from '@/components/scenes/Download';
import { WrappedError } from '@/lib/WrappedError';
import OnlyBotEscrow from '@/contracts/OnlyBotsEscrow.json';
import { NotAllowed } from '@/components/scenes/NotAllowed';
import { UnknownError } from '@/components/scenes/UnknownError';
import { PassedQuizPreview } from '@/components/scenes/PassedQuizPreview';

const CONNECTED_SCENES: string[] = ['PERFORMRECAPTCHA', 'QUIZ'];

const correctChain = {
    1: 'mainnet',
    5: 'goerli',
    1337: 'localhost',
};

type StateMachineProps = {
    scene: OnlybotsScene;
    setScene: (scene: OnlybotsScene) => void;
};

// State machine for determining which user flow step to show
export const SceneStateMachine: VoidFunctionComponent<StateMachineProps> = ({ scene, setScene }) => {
    const [alreadyMinted, setAlreadyMinted] = useState(false);
    const [batchPrice, setBatchPrice] = useState<BigNumber>(BigNumber.from(0));
    const [remainingInBatch, setRemainingInBatch] = useState<number>(0);
    const { navigate } = useRouterContext();
    const { isConnected, address } = useAccount();
    const { getFeatureFlags } = useOnlybotsBackend();
    const [incubatorOpen, setIncubatorOpen] = useState<boolean>(false);
    const [quizPreview, setQuizPreview] = useState<boolean>(false);
    const [isPreviewing, setIsPreviewing] = useState<boolean>(false);
    const { chain } = useNetwork();
    const provider = useProvider();
    const { disconnect } = useDisconnect();
    const [connectionErrorMessage, setConnectionErrorMessage] = useState<string | null>(null);
    const { setPixelate, setMaxPixelGranularity } = useEffectContext();
    const [featuredBots, setFeaturedBots] = useState<FeaturedBot[]>([]);
    const { supportsOrientation, isIOSDevice } = deviceProperties();
    const { orientationPermission, requestAccess: requestOrientation } = useDeviceOrientation();
    const disposer = useDisposeOnUnmount();
    const [playGameStartSound] = useOnlybotsSound(gameStartSound);
    useMusic(scene);
    const { toggleSound } = useMute();
    useKey('m', toggleSound);

    useEffect(() => {
        GA().event('user_flow_step', {
            event_label: scene,
        });
    }, [scene]);

    // handle bad wallet connection
    useEffect(() => {
        if (CONNECTED_SCENES.includes(scene)) {
            if (!isConnected) {
                setConnectionErrorMessage('connection error. please try again');
                setScene('CONNECT');
            }
            if (chain && chain.id !== chainId) {
                setConnectionErrorMessage(`Wrong chain: switch to ${correctChain[chainId]}`);
                setScene('CONNECT');
            }
        } else {
            setConnectionErrorMessage(null);
        }
    }, [address, chain, disconnect, isConnected, scene, setScene]);

    useEffect(() => {
        if (address) {
            (async () => {
                const contract = new ethers.Contract(escrowContractAddress, OnlyBotEscrow.abi, provider);
                const currentClaim: { index: BigNumber; count: BigNumber } = await contract.currentClaim();
                const claimed: { index: BigNumber; count: BigNumber } = await contract.claimed(address);
                if (claimed.index.gte(currentClaim.index) && claimed.count.gte(currentClaim.count)) {
                    setAlreadyMinted(true);
                }
            })().catch((e: any) => {
                console.error(new WrappedError('Error getting claim status', e));
            });
        }
    }, [address, provider]);
    const fetchBatchInfo = useCallback(async () => {
        try {
            const contract = new ethers.Contract(escrowContractAddress, OnlyBotEscrow.abi, provider);
            const batchPriceBN = await contract.price();
            const remainingInBatchBN = await contract.remainingTokens();
            setBatchPrice(batchPriceBN);
            setRemainingInBatch(remainingInBatchBN.toNumber());
        } catch (e: any) {
            console.error(new WrappedError('Error in contract.getCurrentBatchInfo()', e));
        }
    }, [provider]);
    const fetchFeaturedBots = useCallback(async () => {
        try {
            const textureLoader = new TextureLoader();
            let response;
            try {
                response = await fetch(featuredBotsJSON);
            } catch (e) {
                console.error(e);
                try {
                    response = await fetch('/featured-bots.json');
                } catch (e2) {
                    console.error(e2);
                    return;
                }
            }
            const json: FeaturedBotJson[] = await response.json();
            const bots: FeaturedBot[] = [];
            for (const { bot, qrTextureUri, ...rest } of json) {
                try {
                    const onlyBot = OnlyBot.fromJSON(bot);
                    const threeBot = OnlyBotThree.create(onlyBot, presets);
                    disposer.push(threeBot);
                    bots.push({
                        ...rest,
                        qrTexture: qrTextureUri ? await textureLoader.loadAsync(qrTextureUri) : undefined,
                        anchor: bot.anchor,
                        materials: formatVoxelMaterialsFromBot(onlyBot),
                        onlyBotThree: threeBot,
                    });
                    // try not to block UI too much
                    await new Promise((resolve) => setTimeout(resolve, 1));
                } catch (e) {
                    console.error(e);
                }
            }
            bots.forEach(({ qrTexture }) => {
                if (qrTexture) {
                    disposer.push(qrTexture);
                }
            });
            setFeaturedBots(bots);
        } catch (e: any) {
            console.error(e);
        }
    }, [disposer]);

    const fetchFeatureFlags = useCallback(async () => {
        const flags = await getFeatureFlags();
        setIncubatorOpen(flags.incubatorOpen ?? false);
        setQuizPreview(flags.quizPreview ?? false);
    }, [getFeatureFlags]);

    useMount(() => {
        void fetchBatchInfo();
        void fetchFeaturedBots();
        void fetchFeatureFlags();
    });

    useEffect(() => {
        if (scene === 'PASSEDQUIZ' || scene === 'QUIZ') {
            void fetchBatchInfo();
        }
        if (scene === 'TITLE') {
            setIsPreviewing(false);
        }
    }, [scene, fetchBatchInfo]);

    const transitionToScene = useCallback(
        (nextScene: OnlybotsScene) => {
            setPixelate(true);
            setTimeout(() => {
                setScene(nextScene);
                setPixelate(false);
            }, MOSAIC_TRANSITION_DURATION * 1000);
        },
        [setPixelate, setScene]
    );

    switch (scene) {
        case 'LOADING':
            return <Loading onLoaded={() => setScene('STARTSCREEN')} />;
        case 'STARTSCREEN':
            return (
                <StartScreen
                    orientationPermission={orientationPermission}
                    requestOrientation={requestOrientation}
                    goToNextScene={() => {
                        playGameStartSound();
                        setMaxPixelGranularity(true);
                        setScene('TITLE');
                        setTimeout(() => {
                            setMaxPixelGranularity(false);
                        }, MOSAIC_TRANSITION_DURATION * 1000);
                    }}
                />
            );
        case 'TITLE':
            return (
                <TitleScreen
                    featuredBots={featuredBots}
                    continueClick={() => transitionToScene('CODEINPUT')}
                    goToNextScene={() => transitionToScene('STORY')}
                />
            );
        case 'CODEINPUT':
            return (
                <CodeInput
                    straightToConnect={() => transitionToScene('CONNECT')}
                    onToStory={() => transitionToScene('STORY')}
                    backToTitle={() => transitionToScene('TITLE')}
                />
            );
        case 'STORY':
            return (
                <StorySequence
                    goToNextScene={() =>
                        transitionToScene(incubatorOpen && remainingInBatch > 0 ? 'BEGINTEST' : 'INCUBATORCLOSED')
                    }
                />
            );
        case 'BEGINTEST':
            return <BeginTest goToNextScene={() => transitionToScene('CONNECT')} />;
        case 'CONNECT':
            return (
                <Connect
                    goToNextScene={() =>
                        transitionToScene(
                            (incubatorOpen && remainingInBatch > 0) || isPreviewing
                                ? 'PERFORMRECAPTCHA'
                                : 'INCUBATORCLOSED'
                        )
                    }
                    connectionErrorMessage={connectionErrorMessage}
                />
            );
        case 'PERFORMRECAPTCHA':
            return (
                <PerformRecaptcha
                    onNotAllowed={() => transitionToScene('NOTALLOWED')}
                    onError={() => transitionToScene('ERROR')}
                    next={() => {
                        if (alreadyMinted && !isPreviewing) {
                            transitionToScene('ALREADYMINTED');
                        } else {
                            transitionToScene('QUIZ');
                        }
                    }}
                />
            );
        case 'ERROR':
            return <UnknownError cancel={() => transitionToScene('TITLE')} />;
        case 'NOTALLOWED':
            return <NotAllowed cancel={() => transitionToScene('TITLE')} />;
        case 'ALREADYMINTED':
            return <AlreadyMinted cancel={() => transitionToScene('TITLE')} />;
        case 'QUIZ':
            return (
                <Quiz
                    key={address ?? ''}
                    address={address ?? ''}
                    onPass={() => {
                        if (!incubatorOpen && isPreviewing) {
                            transitionToScene('PASSEDQUIZPREVIEW');
                        } else {
                            transitionToScene('PASSEDQUIZ');
                        }
                    }}
                    onCancel={() => transitionToScene('TITLE')}
                />
            );
        case 'PASSEDQUIZ':
            return (
                <Mint
                    cancel={() => transitionToScene('TITLE')}
                    batchPrice={batchPrice}
                    remainingInBatch={remainingInBatch}
                />
            );
        case 'INCUBATORCLOSED':
            return (
                <IncubatorClosed
                    quizPreview={quizPreview}
                    previewQuiz={() => {
                        setIsPreviewing(true);
                        transitionToScene('CONNECT');
                    }}
                    cancel={() => transitionToScene('TITLE')}
                />
            );
        case 'REVEAL':
            return <Reveal cancel={() => navigate('/magicswan')} />;
        case 'EDITIONLOADING':
            return (
                <EditionLoading
                    flowReady={!isIOSDevice || !supportsOrientation || orientationPermission !== undefined}
                    onLoaded={() => {
                        transitionToScene(
                            isIOSDevice && supportsOrientation && orientationPermission === 'notasked'
                                ? 'REQUESTORIENTATION'
                                : 'EDITION'
                        );
                    }}
                />
            );
        case 'REQUESTORIENTATION':
            return (
                <RequestOrientation
                    orientationPermission={orientationPermission}
                    requestOrientation={requestOrientation}
                    onRequested={() => {
                        transitionToScene('EDITION');
                    }}
                />
            );
        case 'EDITION':
            return <Edition />;
        case 'NOTFOUND':
            return <NotFound />;
        case 'SOLDOUT':
            return <SoldOut onCancel={() => transitionToScene('TITLE')} />;
        case 'DOWNLOAD':
            return <Download />;
        case 'PASSEDQUIZPREVIEW':
            return <PassedQuizPreview cancel={() => transitionToScene('TITLE')} />;
        default:
            return (
                <TitleScreen
                    continueClick={() => transitionToScene('CODEINPUT')}
                    goToNextScene={() => transitionToScene('STORY')}
                />
            );
    }
};

type SceneProps = {
    scene: OnlybotsScene;
    setScene: (scene: OnlybotsScene) => void;
};
export const Scene: VoidFunctionComponent<SceneProps> = ({ scene, setScene }) => {
    const { dpr, setDpr } = useDprContext();

    useEffect(() => {
        if (scene === 'EDITION') {
            setDpr(window.devicePixelRatio);
        } else if (dpr !== 1) {
            setDpr(1);
        }
    }, [scene, dpr, setDpr]);

    return (
        <>
            <PostProcessingEffect scene={scene} />
            <SceneStateMachine scene={scene} setScene={setScene} />
        </>
    );
};
